import React, { useMemo } from 'react';
import { View, Text, Button } from 'react-native';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  DatePickerField,
  InputField,
  FormButton,
  DescriptionField,
  CheckBoxField,
  DropdownPickerField,
  TermConditionButotn,
} from './';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import FormLabel from './FormLabel';
import styles from './styles';

enum FieldType {
  text = 'text',
  textarea = 'textArea',
  boolean = 'boolean',
  select = 'select',
  date = 'date',
  terms = 'terms',
  rules = 'rules',
}

type OptionField = {
  label: string;
  value: string;
};

type FieldConfig = {
  fieldType: string;
  label: string;
  name: string;
  required: boolean;
  options: Array<OptionField>;
  optionLabel: string;
  text: string;
};

export type FormProp = {
  title?: string;
  description?: string;
  termsAndConditions?: string;
  rulesAndRegulations?: string;
  startDate?: string;
  endDate?: string;
  fields?: Array<FieldConfig>;
  terms?: string;
  rules?: string;
  onSubmit: any;
  buttonTitle?: string;
  form?: Array<FieldConfig>;
};

const getField = (fieldConfig: FieldConfig, index: number) => {
  const { fieldType, name, required, options, label, optionLabel, text } =
    fieldConfig;
  switch (fieldType) {
    case FieldType.text:
      return (
        <View>
          <InputField
            customLabelStyle={styles.labelDynamicStyle}
            customContainerStyle={styles.inputDynamicStyle}
            customInputStyle={styles.inputInputStyle}
            key={index}
            name={name}
            label={label}
          />
          {required && <Text style={styles.requiredField}>Required field</Text>}
        </View>
      );
    case FieldType.date:
      return <DatePickerField key={index} name={name} label={label} />;
    case FieldType.textarea:
      return (
        <View>
          <DescriptionField
            customInputContainerStyle={styles.dynamicDescriptionInputContainer}
            key={index}
            name={name}
            label={label}
          />
          {required && <Text style={styles.requiredField}>Required field</Text>}
        </View>
      );
    case FieldType.boolean:
      return (
        <>
          <CheckBoxField
            key={index}
            name={name}
            label={label}
            optionLabel={optionLabel}
          />
          {required && (
            <Text style={styles.requiredFieldCheck}>Required field</Text>
          )}
        </>
      );
    case FieldType.select:
      return (
        <View>
          <DropdownPickerField
            customLabelStyle={styles.customPickerLabel}
            key={index}
            {...fieldConfig}
          />
          {required && (
            <Text style={styles.requiredFieldDropdown}>Required field</Text>
          )}
        </View>
      );
    case FieldType.terms:
    case FieldType.rules:
      return text ? (
        <TermConditionButotn
          modalTitle={
            fieldType === FieldType.terms
              ? 'Terms and Conditions'
              : 'Rules and Regulations'
          }
          title={label}
          content={text}
        />
      ) : null;
  }
};

const DynamicForm = ({
  title,
  description,
  fields,
  terms,
  rules,
  onSubmit,
  buttonTitle,
}: FormProp) => {
  const initialValues = useMemo(() => {
    const initial: any = {};
    (fields || []).forEach((element) => {
      if (element.fieldType === FieldType.boolean) {
        if (element.required) {
          initial[element.name] = null;
        } else {
          initial[element.name] = false;
        }
      } else {
        initial[element.name] = '';
      }
    });
    return initial;
  }, [fields]);

  const validationSchema = useMemo(() => {
    let validateObject: any = {};
    (fields || []).forEach((element) => {
      if (element.required) {
        if (element.fieldType === FieldType.boolean) {
          validateObject[element.name] = Yup.bool().oneOf(
            [true],
            `${element.label} is required`,
          );
        } else {
          validateObject[element.name] = Yup.string().required(
            `${element.label} is required`,
          );
        }
      }
    });
    return Yup.object().shape(validateObject);
  }, [fields]);

  return (
    <KeyboardAwareScrollView contentContainerStyle={styles.dynamicFormContent}>
      <View style={styles.titleSection}>
        <FormLabel>{title}</FormLabel>
        <Text style={styles.description}>{description}</Text>
      </View>
      <Formik
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}
        initialValues={initialValues}
      >
        {({ errors, values }) => {
          return (
            <View>
              {(fields || []).map((field: FieldConfig, index) => (
                <View key={index}>
                  {getField(field, index)}
                  <View key={field.name} style={styles.dynamicFormSpacing} />
                </View>
              ))}
              <View style={styles.termsAndRulesContainer}>
                {terms && (
                  <TermConditionButotn
                    modalTitle="Terms and Conditions"
                    title="Terms &amp; Conditions"
                    content={terms}
                  />
                )}
                {rules && (
                  <TermConditionButotn
                    modalTitle="Rules and Regulations"
                    title="Rules and Regulations"
                    content={rules}
                  />
                )}
              </View>
              <FormButton title={buttonTitle || 'Submit'} />
            </View>
          );
        }}
      </Formik>
    </KeyboardAwareScrollView>
  );
};

export default React.memo(DynamicForm);
